import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  applyConversationIngestPreview,
  discardConversationIngestPreview,
  fetchConversationIngestPlanes,
  fetchResearchIngestPlanes,
  modifyConversationIngestPreview,
  planConversationIngest,
  planResearchIngest,
} from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import {
  formatFieldValue,
  formatModificationLine,
  formatRecordPairs,
  previewHasActionableRows,
} from '../utils/conversationIngestFormat';
import './ConversationIngestModal.css';

function toggleIncluded(current) {
  return current === false;
}

function buildSelectionsFromPreview(planes) {
  const field_selections = {};
  const row_selections = {};
  for (const plane of planes || []) {
    const changeIncludedByKey = {};
    for (const row of plane.rows || []) {
      for (const change of row.field_changes || []) {
        if (!changeIncludedByKey[change.key]) {
          changeIncludedByKey[change.key] = [];
        }
        changeIncludedByKey[change.key].push(change.included !== false);
      }
    }
    field_selections[plane.group_slug] = Object.fromEntries(
      (plane.fields || []).map((f) => {
        const votes = changeIncludedByKey[f.key];
        if (votes && votes.length) {
          return [f.key, votes.some(Boolean)];
        }
        return [f.key, f.included !== false];
      })
    );
    row_selections[plane.group_slug] = Object.fromEntries(
      (plane.rows || []).map((r) => [r.row_id || r.entry_id, r.included !== false])
    );
  }
  return { field_selections, row_selections };
}

function IngestRowPreview({ row, groupSlug, onToggleRow, onToggleField }) {
  const rowId = row.row_id || row.entry_id;
  const rowExcluded = row.included === false;
  const before = { ...(row.current_raw || {}) };
  const changesByKey = Object.fromEntries(
    (row.field_changes || []).map((c) => [c.key, c])
  );
  const displayKeys = [...new Set([
    ...Object.keys(before),
    ...Object.keys(changesByKey),
    ...(row.is_new ? Object.keys(row.proposed_raw || {}) : []),
  ])].filter(Boolean).sort();

  return (
    <div className={`conversation-ingest-row-card${rowExcluded ? ' excluded' : ''}`}>
      <div className="conversation-ingest-row-title">
        <span>
          {row.is_new ? 'New row: ' : 'Row: '}
          {row.label || rowId}
        </span>
        <button
          type="button"
          className={`conversation-ingest-chip ${rowExcluded ? 'excluded' : 'included'}`}
          onClick={() => onToggleRow(groupSlug, rowId)}
        >
          <i className="fas fa-times" /> Row
        </button>
      </div>

      <div className="conversation-ingest-snapshot before">
        <div className="conversation-ingest-snapshot-label">Before</div>
        <div className="conversation-ingest-field-grid">
          {displayKeys.map((key) => {
            const hasValue = Object.prototype.hasOwnProperty.call(before, key);
            return (
              <div key={key} className="conversation-ingest-field-pair">
                <span className="field-key">{key}:</span>
                <span className="field-value">{hasValue ? formatFieldValue(before[key]) : '—'}</span>
              </div>
            );
          })}
          {displayKeys.length === 0 && (
            <span className="conversation-ingest-hint">(empty row)</span>
          )}
        </div>
      </div>

      <div className="conversation-ingest-snapshot after">
        <div className="conversation-ingest-snapshot-label">After</div>
        <div className="conversation-ingest-field-grid">
          {displayKeys.map((key) => {
            const change = changesByKey[key];
            const fieldKept = !!change && change.included !== false && !rowExcluded;
            const value = change
              ? change.new_value
              : (Object.prototype.hasOwnProperty.call(before, key) ? before[key] : null);
            return (
              <label
                key={key}
                className={`conversation-ingest-field-pair${change ? ' has-toggle' : ''}${fieldKept ? ' changed' : ''}${change && !fieldKept ? ' rejected' : ''}`}
              >
                {change ? (
                  <input
                    type="checkbox"
                    className="conversation-ingest-field-check"
                    checked={change.included !== false}
                    disabled={rowExcluded}
                    onChange={() => onToggleField(groupSlug, rowId, key)}
                    aria-label={`Keep ${key}`}
                  />
                ) : (
                  <span className="conversation-ingest-field-check-spacer" aria-hidden="true" />
                )}
                <span className="field-key">{key}:</span>
                <span className="field-value">{formatFieldValue(value)}</span>
              </label>
            );
          })}
        </div>
      </div>

      {(row.field_changes || []).length > 0 && (
        <ul className="conversation-ingest-mod-list">
          {(row.field_changes || []).map((change) => {
            const excluded = change.included === false || rowExcluded;
            return (
              <li
                key={change.key}
                className={`conversation-ingest-change${excluded ? ' excluded' : ''}`}
              >
                {formatModificationLine(change)}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function IngestSuccessRecord({ record }) {
  return (
    <div className="conversation-ingest-success-record">
      <h4>{record.group_name || record.group_slug} — {record.label || 'Record'}</h4>
      <p className="conversation-ingest-success-line">
        <strong>Here is the record.</strong>{' '}
        {formatRecordPairs(record.after)}
      </p>
      {record.field_modifications?.length > 0 && (
        <div className="conversation-ingest-success-mods">
          <p><strong>Here are the fields we modified:</strong></p>
          <ul>
            {record.field_modifications.map((mod) => (
              <li key={mod.key}>{formatModificationLine(mod)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ConversationIngestModal({
  open,
  onClose,
  suffix,
  projectId,
  sessionId,
  message,
  researchJobId = null,
  title = 'Ingest conversation into Horizon',
  onOpenFindRecord = null,
}) {
  const navigate = useNavigate();
  const { setCurrentGroup } = useGroup();
  const [step, setStep] = useState('planes');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [planesCatalog, setPlanesCatalog] = useState([]);
  const [constrained, setConstrained] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewId, setPreviewId] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [noRowPlanes, setNoRowPlanes] = useState([]);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyText, setModifyText] = useState('');

  const messageId = message?.message_id;

  const reset = useCallback(() => {
    setStep('planes');
    setError('');
    setSelectedSlugs([]);
    setPreview(null);
    setPreviewId('');
    setApplyResult(null);
    setNoRowPlanes([]);
    setModifyOpen(false);
    setModifyText('');
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = researchJobId
          ? await fetchResearchIngestPlanes({ jobId: researchJobId, projectId, suffix })
          : await fetchConversationIngestPlanes({ suffix, projectId });
        if (cancelled) return;
        setPlanesCatalog(data.planes || []);
        setConstrained(!!data.constrained);
        // Default none selected — users typically pick one plane
        setSelectedSlugs([]);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load ingestion planes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, suffix, projectId, researchJobId, reset]);

  const togglePlane = (slug) => {
    setSelectedSlugs((prev) => (
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    ));
  };

  const openHorizonDataManagement = (groupSlug, { create = false } = {}) => {
    if (groupSlug) setCurrentGroup(groupSlug);
    const params = new URLSearchParams();
    if (groupSlug) params.set('group', groupSlug);
    if (create) params.set('create', '1');
    const qs = params.toString();
    navigate(qs ? `/horizon/data-management?${qs}` : '/horizon/data-management');
    onClose({ applied: false, navigated: true });
  };

  const openFindRecordInChat = (groupSlug) => {
    if (typeof onOpenFindRecord === 'function') {
      onOpenFindRecord({ groupSlug });
      onClose({ applied: false, findRecord: true });
      return;
    }
    openHorizonDataManagement(groupSlug, { create: false });
  };

  const runPlan = async ({ feedback = null, keepSelections = false } = {}) => {
    if (!researchJobId && !messageId) {
      setError('Message id is missing');
      return;
    }
    if (!selectedSlugs.length) {
      setError('Select at least one ingestion plane');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const selections = keepSelections && preview
        ? buildSelectionsFromPreview(preview.planes)
        : { field_selections: {}, row_selections: {} };

      const planFn = feedback
        ? modifyConversationIngestPreview
        : (researchJobId ? planResearchIngest : planConversationIngest);

      const payload = feedback
        ? {
          previewId,
          feedback,
          ...selections,
        }
        : researchJobId
          ? {
            jobId: researchJobId,
            suffix,
            projectId,
            groupSlugs: selectedSlugs,
            ...selections,
          }
          : {
            suffix,
            projectId,
            messageId,
            sessionId,
            groupSlugs: selectedSlugs,
            ...selections,
          };

      const data = await planFn(payload);
      setPreview(data.preview);
      setPreviewId(data.preview_id);
      setModifyOpen(false);
      setModifyText('');

      if (!previewHasActionableRows(data.preview)) {
        const planes = (data.preview?.planes || []).length
          ? data.preview.planes
          : selectedSlugs.map((slug) => planesCatalog.find((p) => p.group_slug === slug)).filter(Boolean);
        setNoRowPlanes(planes);
        setStep('no_row');
      } else {
        setStep('preview');
      }
    } catch (err) {
      setError(
        err?.message
          ? `Could not build the ingestion plan: ${err.message}`
          : 'Could not build the ingestion plan. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const updatePreviewPlane = (groupSlug, updater) => {
    setPreview((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        planes: (prev.planes || []).map((plane) => (
          plane.group_slug === groupSlug ? updater(plane) : plane
        )),
      };
    });
  };

  const toggleRowField = (groupSlug, rowId, key) => {
    updatePreviewPlane(groupSlug, (plane) => ({
      ...plane,
      rows: (plane.rows || []).map((r) => {
        const id = r.row_id || r.entry_id;
        if (id !== rowId) return r;
        return {
          ...r,
          field_changes: (r.field_changes || []).map((c) => (
            c.key === key ? { ...c, included: toggleIncluded(c.included) } : c
          )),
        };
      }),
    }));
  };

  const toggleRow = (groupSlug, rowId) => {
    updatePreviewPlane(groupSlug, (plane) => ({
      ...plane,
      rows: (plane.rows || []).map((r) => {
        const id = r.row_id || r.entry_id;
        if (id !== rowId) return r;
        return { ...r, included: toggleIncluded(r.included) };
      }),
    }));
  };

  const handleApply = async () => {
    if (!previewId || !preview) return;
    setLoading(true);
    setError('');
    try {
      const data = await applyConversationIngestPreview({ previewId, planes: preview.planes });
      setApplyResult(data);
      if ((data.records || []).length > 0) {
        setStep('success');
      } else if ((data.errors || []).length > 0) {
        setError(data.errors.map((e) => e.error).join('; ') || 'Failed to apply ingestion');
      } else {
        setNoRowPlanes(preview.planes || []);
        setStep('no_row');
      }
    } catch (err) {
      setError(err.message || 'Failed to apply ingestion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (previewId && step !== 'success') {
      try {
        await discardConversationIngestPreview(previewId);
      } catch (_) { /* ignore */ }
    }
    onClose({ applied: false });
  };

  const handleDone = () => {
    onClose({ applied: true, result: applyResult });
  };

  if (!open) return null;

  return (
    <div className="conversation-ingest-overlay" role="presentation">
      <div
        className="conversation-ingest-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversation-ingest-title"
      >
        <div className="conversation-ingest-header">
          <h3 id="conversation-ingest-title">{title}</h3>
          <p className="conversation-ingest-hint">
            {step === 'success'
              ? 'Ingestion was applied to your horizon data.'
              : step === 'no_row'
                ? 'No matching row was found to update from this content.'
                : 'Review proposed gap-fills against existing ingestion rows before applying.'}
            {step === 'planes' && constrained ? ' Planes are limited by this project\'s configuration.' : ''}
          </p>
        </div>

        <div className="conversation-ingest-body">
          {loading && <p><i className="fas fa-spinner fa-spin" /> Working…</p>}
          {error && <p className="conversation-ingest-error">{error}</p>}

          {step === 'planes' && !loading && (
            <>
              <p>Select ingestion plane(s) for this message:</p>
              <div className="conversation-ingest-plane-list">
                {planesCatalog.map((plane) => {
                  const selected = selectedSlugs.includes(plane.group_slug);
                  return (
                    <label
                      key={plane.group_slug}
                      className={`conversation-ingest-plane-item${selected ? ' selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => togglePlane(plane.group_slug)}
                      />
                      <span>
                        <strong>{plane.name}</strong>
                        <div className="conversation-ingest-hint">{plane.group_slug}</div>
                      </span>
                    </label>
                  );
                })}
                {planesCatalog.length === 0 && (
                  <p className="conversation-ingest-hint">No ingestion planes available for this project.</p>
                )}
              </div>
            </>
          )}

          {step === 'preview' && preview && (
            <>
              {preview.reasoning && (
                <p className="conversation-ingest-hint"><strong>Plan:</strong> {preview.reasoning}</p>
              )}

              {(preview.planes || []).map((plane) => (
                <div key={plane.group_slug} className="conversation-ingest-plane-block">
                  <h4>{plane.name}</h4>

                  {(plane.rows || []).length === 0 && (
                    <p className="conversation-ingest-hint">No gap-fills proposed for this plane.</p>
                  )}

                  {(plane.rows || []).map((row) => (
                    <IngestRowPreview
                      key={row.row_id || row.entry_id}
                      row={row}
                      groupSlug={plane.group_slug}
                      onToggleRow={toggleRow}
                      onToggleField={toggleRowField}
                    />
                  ))}
                </div>
              ))}

              {modifyOpen && (
                <div className="conversation-ingest-modify">
                  <label htmlFor="ingest-modify-feedback">
                    <strong>Modify ingestion</strong> — tell the model what to change:
                  </label>
                  <textarea
                    id="ingest-modify-feedback"
                    value={modifyText}
                    onChange={(e) => setModifyText(e.target.value)}
                    placeholder="e.g. Only update revenue fields; Acme Corp should map to the existing row with slug acme-corp"
                  />
                </div>
              )}
            </>
          )}

          {step === 'success' && applyResult && (
            <div className="conversation-ingest-success">
              <p className="conversation-ingest-success-banner">
                <i className="fas fa-check-circle" /> INGESTION SUCCESSFUL
              </p>
              {(applyResult.records || []).map((record) => (
                <IngestSuccessRecord
                  key={`${record.group_slug}-${record.entry_id || record.row_id}`}
                  record={record}
                />
              ))}
            </div>
          )}

          {step === 'no_row' && (
            <div className="conversation-ingest-no-row">
              <p className="conversation-ingest-no-row-prompt">
                Row was not available. Do you wish to search for it, or do you wish to create a new row?
              </p>
              <p className="conversation-ingest-hint">
                Search for an existing row in this chat, or open Horizon Data Management to create a new one.
              </p>
              <div className="conversation-ingest-no-row-actions">
                {(noRowPlanes.length ? noRowPlanes : planesCatalog.filter((p) => selectedSlugs.includes(p.group_slug))).map((plane) => (
                  <div key={plane.group_slug} className="conversation-ingest-no-row-plane">
                    <strong>{plane.name || plane.group_slug}</strong>
                    <div className="conversation-ingest-no-row-buttons">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => openFindRecordInChat(plane.group_slug)}
                      >
                        <i className="fas fa-search" /> Search for row
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => openHorizonDataManagement(plane.group_slug, { create: true })}
                      >
                        <i className="fas fa-plus" /> Create new row
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="conversation-ingest-footer">
          {step === 'success' ? (
            <button type="button" className="btn" onClick={handleDone} disabled={loading}>
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn ghost" onClick={handleCancel} disabled={loading}>
                {step === 'no_row' ? 'Close' : 'Cancel'}
              </button>

              {step === 'planes' && (
                <button
                  type="button"
                  className="btn"
                  disabled={loading || !selectedSlugs.length}
                  onClick={() => runPlan()}
                >
                  Review gaps
                </button>
              )}

              {step === 'preview' && (
                <>
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={loading}
                    onClick={() => setModifyOpen((v) => !v)}
                  >
                    Modify ingestion
                  </button>
                  {modifyOpen && (
                    <button
                      type="button"
                      className="btn ghost"
                      disabled={loading || !modifyText.trim()}
                      onClick={() => runPlan({ feedback: modifyText.trim(), keepSelections: true })}
                    >
                      Re-plan with feedback
                    </button>
                  )}
                  <button type="button" className="btn" disabled={loading} onClick={handleApply}>
                    Okay — apply updates
                  </button>
                </>
              )}

              {step === 'no_row' && (
                <button
                  type="button"
                  className="btn ghost"
                  disabled={loading}
                  onClick={() => setStep('planes')}
                >
                  Try again
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
